import React, { useState } from "react";
import { Tooltip } from "@/components";
import { DenmarkPublish as DenmarkPublishInterface } from "@/database";
import { formatTimestampToAgo, getHostLogoStyleByHostId } from "@/utils";
import { renderStatusIndicator } from "@/utils/mediautils/RenderStatusIndicator";
import DenmarkMultiPublishChips from "./DenmarkMultiPublishChips";

type Props = {
  // Chips data
  currentSelectedSection: DenmarkPublishInterface[];
  selectedSections: DenmarkPublishInterface[];
  socials: any[];

  // Host/logo helpers
  hosts: any[];
  hostOptions: any[];
  denmarkHosts: any[];

  // Flags
  isDraft?: boolean;
  isPreset?: boolean;
  readOnlyMode?: boolean;
  isPreview?: boolean;
  showPopover: boolean;

  // Callbacks
  onChipClick: (item: DenmarkPublishInterface) => void;
  onCancelChip: (sectionId: number) => void;
  onDeleteDraftChip: (item: DenmarkPublishInterface) => void;
  onPopoverChipClick?: (chip: DenmarkPublishInterface, anchor: DOMRect) => void;
  onMultiChipClick: (parent: DenmarkPublishInterface) => void;
  onMultiPopoverChipClick?: (
    target: DenmarkPublishInterface,
    parent: DenmarkPublishInterface
  ) => void;
  onDeleteMultiTarget?: (
    target: DenmarkPublishInterface,
    parent: DenmarkPublishInterface
  ) => void;
};

const DenmarkChipsDisplay: React.FC<Props> = ({
  currentSelectedSection,
  selectedSections,
  socials,
  hosts,
  hostOptions,
  denmarkHosts,
  isDraft,
  isPreset,
  readOnlyMode,
  isPreview,
  showPopover,
  onChipClick,
  onCancelChip,
  onDeleteDraftChip,
  onPopoverChipClick,
  onMultiChipClick,
  onMultiPopoverChipClick,
  onDeleteMultiTarget,
}) => {
  const [popoverChip, setPopoverChip] = useState<{
    chip: DenmarkPublishInterface;
    anchor: DOMRect;
  } | null>(null);

  // Collect all section IDs that are multi-publish targets embedded inside
  // a saved parent (these are shown in the "Multi-Publish" section, not as
  // standalone chips).
  const allSavedDenmark = (socials || []).filter(
    (s) => s.job_type === "denmark"
  ) as DenmarkPublishInterface[];

  const multiTargetSectionIds = new Set<number>();
  for (const parent of allSavedDenmark) {
    if (Array.isArray(parent.multi_publish_targets)) {
      for (const t of parent.multi_publish_targets) {
        multiTargetSectionIds.add(Number(t.section));
      }
    }
  }

  // Published multi-publish targets that have graduated to standalone
  // selectedSections entries (they have their own social record now).
  const publishedMultiTargetChips = (selectedSections || []).filter(
    (item) =>
      item.job_type === "denmark" &&
      multiTargetSectionIds.has(Number(item.section))
  );

  const publishedMultiTargetSectionIds = new Set(
    publishedMultiTargetChips.map((c) => Number(c.section))
  );

  /** `status === 100` = not yet published; any other numeric status = saved to server. */
  const isSavedDenmarkSocial = (s: { job_type?: string; status?: number }) =>
    s?.job_type === "denmark" &&
    typeof s.status === "number" &&
    s.status !== 100;

  const hasPublishedMediaDenmark =
    (socials || []).some(isSavedDenmarkSocial) ||
    (selectedSections || []).some(isSavedDenmarkSocial);

  const formDenmarkPicks = (currentSelectedSection || []).filter(
    (item) => item.job_type === "denmark" || isPreset
  );

  const showFormPicksInTopRow = !hasPublishedMediaDenmark;
  const showFormPicksInMultiBlock =
    hasPublishedMediaDenmark && formDenmarkPicks.length > 0;

  const hasMultiPublishSection =
    hasPublishedMediaDenmark &&
    (multiTargetSectionIds.size > 0 ||
      publishedMultiTargetChips.length > 0 ||
      formDenmarkPicks.length > 0);

  const renderDraftDeleteButton = (item: DenmarkPublishInterface) => (
    <i
      className="bi bi-x fs-4"
      style={{ cursor: "pointer" }}
      onClick={(e) => {
        e.stopPropagation();
        onDeleteDraftChip(item);
      }}
    />
  );

  return (
    <div className="g-2 mt-2 d-flex flex-column gap-2">
      {/* ── Main denmark chips ── */}
      <div className="d-flex gap-2" style={{ flexWrap: "wrap" }}>
        {/* In-form picks (draft/new) — only on top when media has no published Denmark yet */}
        {showFormPicksInTopRow &&
          formDenmarkPicks.map((item) => (
            <div
              key={`form-${item.section}`}
              className="social-chip gap-1 p-1"
              style={{
                cursor: "pointer",
                position: "relative",
                width: "46%",
              }}
            >
              <div
                className="d-flex align-items-center gap-1"
                style={{ width: "calc(100% - 25px)" }}
                onClick={() => onChipClick(item)}
              >
                {(() => {
                  const logoStyle = getHostLogoStyleByHostId(
                    item.host,
                    hosts,
                    hostOptions,
                    denmarkHosts
                  );
                  return <div style={logoStyle} />;
                })()}
                <span className="text-truncate flex-grow-1">
                  {item.channel_name}
                </span>
              </div>
              {!readOnlyMode && !isPreview && (
                <i
                  className="bi bi-x fs-4"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancelChip(item.section);
                    if (popoverChip?.chip.section === item.section) {
                      setPopoverChip(null);
                    }
                  }}
                />
              )}
            </div>
          ))}

        {/* Published/queued socials from Redux (not in form list, not a multi-publish target) */}
        {selectedSections &&
          selectedSections
            .filter((item) => item.job_type === "denmark" || isPreset)
            .filter(
              (item) =>
                !currentSelectedSection?.some(
                  (chip) => chip.section === item.section
                )
            )
            .filter(
              (item) =>
                !publishedMultiTargetSectionIds.has(Number(item.section))
            )
            .sort((a, b) => (a.ct ?? 0) - (b.ct ?? 0))
            .map((item) => (
              <div
                key={`redux-${item.section}`}
                className="social-chip gap-1 p-1"
                style={{
                  cursor: "pointer",
                  position: "relative",
                  maxWidth: "46%",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onPopoverChipClick) {
                    const rect = (
                      e.currentTarget as HTMLElement
                    ).getBoundingClientRect();
                    if (popoverChip?.chip.section === item.section) {
                      setPopoverChip(null);
                    } else {
                      setPopoverChip({ chip: item, anchor: rect });
                      onPopoverChipClick(item, rect);
                    }
                  }
                }}
              >
                {(() => {
                  const logoStyle = getHostLogoStyleByHostId(
                    item.host,
                    hosts,
                    hostOptions,
                    denmarkHosts
                  );
                  return <div style={logoStyle} />;
                })()}

                <span
                  className="text-truncate flex-grow-1"
                  style={{ paddingRight: "10px" }}
                >
                  {item.channel_name}
                </span>
                {!isDraft && (
                  <Tooltip
                    content={
                      item?.schedule &&
                      item.schedule > Date.now() &&
                      item.status === 10
                        ? `Scheduled On: ${new Date(
                            item.schedule
                          ).toLocaleString()}`
                        : item?.status === 10
                          ? `Published On: ${formatTimestampToAgo(
                              item?.schedule || item?.mt
                            )}`
                          : item?.status === -2 || item?.status === -1
                            ? (item?.msg ??
                              "Your content has failed to publish.")
                            : item?.status === 0
                              ? "Your content is in queue, uploading will begin once content is activated."
                              : "Your content is getting uploaded, currently in progress."
                    }
                  >
                    {item?.schedule &&
                    item.schedule > Date.now() &&
                    item.status === 10 ? (
                      <i
                        className="bi bi-clock text-secondary me-1"
                        style={{ fontSize: "1rem" }}
                      ></i>
                    ) : (
                      renderStatusIndicator(item.status)
                    )}
                  </Tooltip>
                )}
                {isDraft && renderDraftDeleteButton(item)}
              </div>
            ))}
      </div>

      {/* ── Multi-Publish section ── */}
      {hasMultiPublishSection && (
        <div className="mt-1">
          <label>Multi-Publish</label>
          <div className="d-flex gap-2" style={{ flexWrap: "wrap" }}>
            {/* Embedded multi-publish target chips (not yet standalone) */}
            <DenmarkMultiPublishChips
              socials={socials}
              hosts={hosts}
              hostOptions={hostOptions}
              denmarkHosts={denmarkHosts}
              isDraft={isDraft}
              showPopover={showPopover}
              onChipClick={onMultiChipClick}
              onPopoverChipClick={(target, parent, _rect) =>
                onMultiPopoverChipClick?.(target, parent)
              }
              onDeleteMultiTarget={onDeleteMultiTarget}
            />

            {showFormPicksInMultiBlock &&
              formDenmarkPicks.map((item) => (
                <div
                  key={`form-multi-${item.section}`}
                  className="social-chip gap-1 p-1"
                  style={{
                    cursor: "pointer",
                    position: "relative",
                    width: "46%",
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-1"
                    style={{ width: "calc(100% - 25px)" }}
                    onClick={() => onChipClick(item)}
                  >
                    {(() => {
                      const logoStyle = getHostLogoStyleByHostId(
                        item.host,
                        hosts,
                        hostOptions,
                        denmarkHosts
                      );
                      return <div style={logoStyle} />;
                    })()}
                    <span className="text-truncate flex-grow-1">
                      {item.channel_name}
                    </span>
                  </div>
                  {!readOnlyMode && !isPreview && (
                    <i
                      className="bi bi-x fs-4"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCancelChip(item.section);
                        if (popoverChip?.chip.section === item.section) {
                          setPopoverChip(null);
                        }
                      }}
                    />
                  )}
                </div>
              ))}

            {/* Published multi-publish targets that now have their own social record */}
            {publishedMultiTargetChips
              .sort((a, b) => (a.ct ?? 0) - (b.ct ?? 0))
              .map((item) => (
                <div
                  key={`multi-published-${item.section}`}
                  className="social-chip gap-1 p-1"
                  style={{
                    cursor: showPopover ? "pointer" : "default",
                    position: "relative",
                    maxWidth: "46%",
                  }}
                  onClick={(e) => {
                    if (!showPopover) return;
                    e.stopPropagation();
                    if (onPopoverChipClick) {
                      const rect = (
                        e.currentTarget as HTMLElement
                      ).getBoundingClientRect();
                      if (popoverChip?.chip.section === item.section) {
                        setPopoverChip(null);
                      } else {
                        setPopoverChip({ chip: item, anchor: rect });
                        onPopoverChipClick(item, rect);
                      }
                    }
                  }}
                >
                  {(() => {
                    const logoStyle = getHostLogoStyleByHostId(
                      item.host,
                      hosts,
                      hostOptions,
                      denmarkHosts
                    );
                    return <div style={logoStyle} />;
                  })()}
                  <span
                    className="text-truncate flex-grow-1"
                    style={{ paddingRight: "10px" }}
                  >
                    {item.channel_name}
                  </span>
                  {!isDraft && (
                    <Tooltip
                      content={
                        item?.schedule &&
                        item.schedule > Date.now() &&
                        item.status === 10
                          ? `Scheduled On: ${new Date(
                              item.schedule
                            ).toLocaleString()}`
                          : item?.status === 10
                            ? `Published On: ${formatTimestampToAgo(
                                item?.schedule || item?.mt
                              )}`
                            : item?.status === -2 || item?.status === -1
                              ? (item?.msg ??
                                "Your content has failed to publish.")
                              : item?.status === 0
                                ? "Your content is in queue, uploading will begin once content is activated."
                                : "Your content is getting uploaded, currently in progress."
                      }
                    >
                      {item?.schedule &&
                      item.schedule > Date.now() &&
                      item.status === 10 ? (
                        <i
                          className="bi bi-clock text-secondary me-1"
                          style={{ fontSize: "1rem" }}
                        ></i>
                      ) : (
                        renderStatusIndicator(item.status)
                      )}
                    </Tooltip>
                  )}
                  {isDraft && renderDraftDeleteButton(item)}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DenmarkChipsDisplay;
